import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { THandleAction } from 'ui/EasTable/types';
import { DeleteDialog } from 'ui/DeleteDialog';
import {
	getAssignedScripts,
	getAssignedScriptsLoading,
	getAssignScriptLoading,
	getRemoveScriptLoading,
} from 'redux/reducers/emAutomationScripts/selectors';
import {
	getAssignedScriptsRequest,
	removeScriptRequest,
} from 'redux/reducers/emAutomationScripts/reducer';
import { TEmAutomationAssignedScriptsRequest } from 'redux/reducers/emAutomationScripts/types';
import { tableStructure } from './tableStructure';
import { AddScriptDialog } from './AddScriptDialog';

export const EmAutomationScripts = () => {
	const { emId } = useParams<{ emId?: string }>();
	const dispatch = useDispatch();

	const assignedScripts = useSelector(getAssignedScripts);
	const assignedScriptsLoading = useSelector(getAssignedScriptsLoading);
	const assignScriptLoading = useSelector(getAssignScriptLoading);
	const removeScriptLoading = useSelector(getRemoveScriptLoading);

	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [deleteId, setDeleteId] = useState<number | null>(null);

	const getAssignedScriptsRequestPayload: TEmAutomationAssignedScriptsRequest = useMemo(
		() => ({
			emId: Number(emId),
			body: {
				pageNumber: 1,
				pageSize: PAGE_SIZE,
			},
		}),
		[emId],
	);

	useEffect(() => {
		if (emId) {
			dispatch(getAssignedScriptsRequest(getAssignedScriptsRequestPayload));
		}
	}, [dispatch, emId, getAssignedScriptsRequestPayload]);

	const handleAction: THandleAction = useCallback((id, action) => {
		if (action === 'Delete') {
			setDeleteId(id);
			setOpenDeleteDialog(true);
		}
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (!deleteId || !emId) return;
		dispatch(
			removeScriptRequest({
				id: deleteId,
				emId: Number(emId),
				getAssignedScriptsRequest: getAssignedScriptsRequestPayload,
			}),
		);
		setOpenDeleteDialog(false);
		setDeleteId(null);
	}, [dispatch, deleteId, emId, getAssignedScriptsRequestPayload]);

	const isLoading = useMemo(
		() => assignedScriptsLoading || assignScriptLoading || removeScriptLoading,
		[assignedScriptsLoading, assignScriptLoading, removeScriptLoading],
	);

	return (
		<Box>
			<EasTableWithoutFilter
				tableStructure={tableStructure}
				items={assignedScripts}
				itemsIsLoading={isLoading}
				handleAction={handleAction}
				skeletonRows={[1]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Automations
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{isLoading && <CircularProgress size={20} />}
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => setOpenAddDialog(true)}
						>
							Add Automation
						</Button>
					</Box>
				</Box>
			</EasTableWithoutFilter>

			<AddScriptDialog
				open={openAddDialog}
				setClose={() => setOpenAddDialog(false)}
				getAssignedScriptsRequest={getAssignedScriptsRequestPayload}
			/>

			<DeleteDialog
				open={openDeleteDialog}
				setClose={() => {
					setOpenDeleteDialog(false);
					setDeleteId(null);
				}}
				handleDelete={handleConfirmDelete}
				text="Are you sure you want to remove this automation script?"
			/>
		</Box>
	);
};
